import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Card from '../../components/Card';
import AnalysisFormatter from './AnalysisFormatter';

const ResultsContainer = styled(Card)`
  margin-top: 40px;
  margin-bottom: 40px;
  padding: 30px;
  width: 100%;
  position: relative;
  max-width: 100%;
  
  &::before {
    content: "💕";
    position: absolute;
    top: -15px;
    right: 80px;
    font-size: 30px;
    filter: drop-shadow(0 3px 5px rgba(0,0,0,0.3));
    animation: float 4s infinite ease-in-out;
    z-index: 2;
    pointer-events: none;
  }
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
`;

const ResultsTitle = styled.h2`
  font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
  font-size: 2.2rem;
  color: var(--primary-color);
  font-weight: 600;
  margin: 0;
  text-align: center;
  text-shadow: 0 2px 10px rgba(255, 64, 192, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const DownloadButton = styled.button`
  background-color: var(--secondary-color);
  color: #170032;
  border: none;
  border-radius: 30px;
  padding: 10px 20px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(255, 208, 0, 0.3);
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 0.9rem;
  }
  
  @media (max-width: 360px) {
    .button-text {
      display: none; /* Hide text on very small screens */
    }
    padding: 8px;
    gap: 0;
  }
  
  &:hover {
    background-color: #FFE03D;
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(255, 208, 0, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  i {
    font-size: 1.1rem;
  }
`;

const LoadingIndicator = styled.div`
  font-size: 1.2rem;
  color: var(--secondary-color);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  padding: 30px 0;
  position: relative;
  
  .loader {
    position: relative;
    width: 80px;
    height: 80px;
    margin-bottom: 20px;
  }
  
  .loader-circle {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 4px solid rgba(255, 208, 0, 0.1);
    border-top: 4px solid var(--secondary-color);
    border-radius: 50%;
    animation: spin 1.5s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  &::after {
    content: "";
    position: absolute;
    width: 150px;
    height: 150px;
    background: radial-gradient(circle, rgba(255, 208, 0, 0.15) 0%, rgba(255, 208, 0, 0) 70%);
    border-radius: 50%;
    z-index: -1;
    pointer-events: none;
    filter: blur(15px);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    animation: pulse 2s infinite ease-in-out alternate;
  }
  
  span {
    font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
    text-shadow: 0 2px 8px rgba(255, 208, 0, 0.4);
    letter-spacing: 0.5px;
  }
`;

const ErrorDisplay = styled.div`
  color: #ff4f4f;
  background-color: rgba(255, 79, 79, 0.15);
  padding: 20px;
  border-radius: 15px;
  font-size: 1.1rem;
  text-align: center;
  white-space: pre-wrap;
  box-shadow: 0 5px 15px rgba(255, 79, 79, 0.2);
  border: 1px solid rgba(255, 79, 79, 0.3);
`;

const AnalysisResultsContent = styled.div`
  /* Questo wrapper serve per contenere l'header e il formatter,
     ma non il pulsante di download. Sarà il ResultsContainer ad essere catturato. */
`;

// Component dedicated to analysis results
const AnalysisResults = ({ 
  loading,
  results,
  analysisError,
  visible,
}) => {
  const resultsRef = useRef(null);
  const downloadButtonRef = useRef(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  
  // Debug: monitoring received props
  useEffect(() => {
    console.log("AnalysisResults: Props changed", { loading, results: !!results, analysisError, visible });
  }, [loading, results, analysisError, visible]);

  // If there are no results to show and it's not loading, check if there are results saved in localStorage
  useEffect(() => {
    if (!visible && !loading && !results && !analysisError) {
      const savedResults = localStorage.getItem('analysisResults');
      if (savedResults) {
        try {
          const { results: savedResultText } = JSON.parse(savedResults);
          if (savedResultText) {
            console.log("AnalysisResults: Found saved results in localStorage, but component is not visible");
          }
        } catch (e) {
          console.error("AnalysisResults: Error parsing saved results:", e);
        }
      }
    }
  }, [visible, loading, results, analysisError]);
  
  // Function to generate and download the PDF
  const handleDownloadPdf = async () => {
    if (!resultsRef.current || isGeneratingPdf) return;
    
    setIsGeneratingPdf(true);
    const button = downloadButtonRef.current;
    let originalButtonStyleDisplay = '';

    if (button) {
      originalButtonStyleDisplay = button.style.display;
      button.style.display = 'none';
    }
    
    try {
      // Attendi un breve istante per assicurarti che il DOM sia aggiornato (bottone nascosto)
      await new Promise(resolve => setTimeout(resolve, 50));

      const canvas = await html2canvas(resultsRef.current, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: '#140523',
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.7);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgProps = pdf.getImageProperties(imgData);
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
      
      let heightLeft = imgHeight;
      let position = 0;
      
      // Imposta lo sfondo per la prima pagina
      pdf.setFillColor('#140523');
      pdf.rect(0, 0, pdfWidth, pdfHeight, 'F'); // 'F' sta per Fill
      pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;
      
      while (heightLeft > 0) {
        position -= pdfHeight;
        pdf.addPage();
        // Imposta lo sfondo per le nuove pagine
        pdf.setFillColor('#140523');
        pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');
        pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;
      }
      
      // Aggiungi footer
      pdf.setFont(undefined, 'bold'); // Imposta il font in grassetto per il footer
      pdf.setFontSize(8);
      
      const footerTextPart1 = "Automatically generated by ";
      const footerTextPart2 = "InstaRizz";
      
      const textPart1Width = pdf.getStringUnitWidth(footerTextPart1) * pdf.getFontSize() / pdf.internal.scaleFactor;
      const textPart2Width = pdf.getStringUnitWidth(footerTextPart2) * pdf.getFontSize() / pdf.internal.scaleFactor;
      const totalTextWidth = textPart1Width + textPart2Width;
      
      const startX = (pdfWidth - totalTextWidth) / 2;
      const textY = pdfHeight - 10; // 10mm dal fondo
      
      let lastPageNum = pdf.internal.getNumberOfPages();
      pdf.setPage(lastPageNum);

      // Disegna la prima parte in bianco
      pdf.setTextColor(255, 255, 255); // Bianco
      pdf.text(footerTextPart1, startX, textY);
      
      // Disegna la seconda parte in viola
      pdf.setTextColor(255, 64, 192); // Viola (#FF40C0)
      pdf.text(footerTextPart2, startX + textPart1Width, textY);

      pdf.save('instarizz-analysis.pdf');
    } catch (error) {
      console.error('Error generating PDF with html2canvas:', error);
    } finally {
      if (button) {
        button.style.display = originalButtonStyleDisplay; // Ripristina lo stile del bottone
      }
      setIsGeneratingPdf(false);
    }
  };

  // If it's not visible, don't render anything
  if (!visible) {
    console.log("AnalysisResults: Component not visible, returning null");
    return null;
  }

  return (
    <ResultsContainer ref={resultsRef}>
      <AnalysisResultsContent>
        <ResultsHeader>
          <ResultsTitle>Analysis Results</ResultsTitle>
        </ResultsHeader>
        
        {loading && (
          <LoadingIndicator>
            <div className="loader">
              <div className="loader-circle"></div>
            </div>
            <span>Analysis in progress, please wait...</span>
          </LoadingIndicator>
        )}
        
        {!loading && analysisError && (
          <ErrorDisplay>{analysisError}</ErrorDisplay>
        )}
        
        {!loading && !analysisError && results && (
          <AnalysisFormatter analysisText={results} />
        )}
        
        {!loading && !analysisError && !results && (
          <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem', marginTop: '20px' }}>
            No results to display or analysis not yet started.
          </p>
        )}
      </AnalysisResultsContent>
      
      {!loading && !analysisError && results && (
        <DownloadButton ref={downloadButtonRef} onClick={handleDownloadPdf} disabled={isGeneratingPdf || !results} style={{ margin: '20px auto 0' }}>
          {isGeneratingPdf ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-download"></i>}
          <span className="button-text">
            {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
          </span>
        </DownloadButton>
      )}
    </ResultsContainer>
  );
};

export default AnalysisResults; 